import React, { useState, useEffect, useRef, useCallback } from 'react';
import { XIcon, MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, CaretLeftIcon, CaretRightIcon, PencilIcon, DownloadIcon } from '@phosphor-icons/react';

export interface ViewerImage {
    url: string;
    label?: string;
    id?: string | number;
    metadata?: Record<string, any>;
}

interface FullscreenImageViewerProps {
    images: ViewerImage[];
    currentIndex: number | null;
    onClose: () => void;
    onNavigate?: (index: number) => void;
    /** Optional render prop for action buttons in the control bar */
    renderActions?: (image: ViewerImage, index: number) => React.ReactNode;
    /** If true, uses fixed positioning to cover entire viewport (default). If false, uses absolute positioning within parent. */
    fixed?: boolean;
    /** If true, respects sidebar CSS variables (--sidebar-left, --sidebar-right) for positioning */
    respectSidebars?: boolean;
    /** Callback for Edit button (inpainting). If provided, shows Edit button */
    onEdit?: (image: ViewerImage, index: number) => void;
    /** Callback for Download button. If provided, shows Download button */
    onDownload?: (image: ViewerImage, index: number) => void;
    /** Function to check if a specific image supports inpainting */
    supportsInpainting?: (image: ViewerImage, index: number) => boolean;
}

export const FullscreenImageViewer: React.FC<FullscreenImageViewerProps> = ({
    images,
    currentIndex,
    onClose,
    onNavigate,
    renderActions,
    fixed = true,
    respectSidebars = false,
    onEdit,
    onDownload,
    supportsInpainting
}) => {
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const viewerRef = useRef<HTMLDivElement>(null);

    const currentImage = currentIndex !== null ? images[currentIndex] : null;
    const hasMultiple = images.length > 1;

    // Reset zoom/pan when image changes
    useEffect(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    }, [currentIndex]);

    const goToPrev = useCallback(() => {
        if (currentIndex === null || images.length === 0) return;
        const newIndex = (currentIndex - 1 + images.length) % images.length;
        onNavigate?.(newIndex);
    }, [currentIndex, images.length, onNavigate]);

    const goToNext = useCallback(() => {
        if (currentIndex === null || images.length === 0) return;
        const newIndex = (currentIndex + 1) % images.length;
        onNavigate?.(newIndex);
    }, [currentIndex, images.length, onNavigate]);

    // Keyboard navigation
    useEffect(() => {
        if (currentIndex === null) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    goToPrev();
                    break;
                case 'ArrowRight':
                    goToNext();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex, onClose, goToPrev, goToNext]);

    // Minimum zoom is 1 (100%) = fit to container, max is 5 (500%)
    const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 5));
    const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 1));
    const handleResetZoom = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
        setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isDragging) {
            e.preventDefault();
            setPan({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => setIsDragging(false);

    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.25 : 0.25;
        setZoom((prev) => Math.min(Math.max(prev + delta, 1), 5)); // Min 100%, Max 500%
    };

    if (currentIndex === null || !currentImage) return null;

    const positionClass = fixed ? 'fixed' : 'absolute';

    // Build positioning style
    const positionStyle: React.CSSProperties = respectSidebars
        ? {
            left: 'var(--sidebar-left, 0px)',
            right: 'var(--sidebar-right, 0px)',
            top: 0,
            bottom: 0
        }
        : {};

    return (
        <div
            className={`${positionClass} ${respectSidebars ? '' : 'inset-0'} z-[100] bg-onyx flex flex-col`}
            style={positionStyle}
            onClick={onClose}
        >
            {/* Control Bar */}
            <div className="h-12 flex items-center justify-between px-4 bg-carbon border-b border-graphite shrink-0">
                <div className="flex items-center gap-3">
                    {currentImage.label && (
                        <div className="text-sm font-medium text-dust">{currentImage.label}</div>
                    )}
                    {hasMultiple && (
                        <span className="text-xs text-iron">
                            {currentIndex + 1} / {images.length}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {/* Edit Button (Inpainting) */}
                    {onEdit && supportsInpainting?.(currentImage, currentIndex) && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onEdit(currentImage, currentIndex); }}
                            className="w-9 h-9 rounded-lg bg-graphite hover:bg-indigo-600 flex items-center justify-center transition-colors"
                            title="Edit with Inpainting"
                        >
                            <PencilIcon className="w-5 h-5 text-dust" />
                        </button>
                    )}

                    {/* Download Button */}
                    {onDownload && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onDownload(currentImage, currentIndex); }}
                            className="w-9 h-9 rounded-lg bg-graphite hover:bg-iron flex items-center justify-center transition-colors"
                            title="Download"
                        >
                            <DownloadIcon className="w-5 h-5 text-dust" />
                        </button>
                    )}

                    {/* Custom Actions */}
                    {renderActions && (
                        <>
                            {(onEdit || onDownload) && <div className="w-px h-6 bg-graphite mx-1" />}
                            <div onClick={(e) => e.stopPropagation()}>
                                {renderActions(currentImage, currentIndex)}
                            </div>
                            <div className="w-px h-6 bg-graphite mx-1" />
                        </>
                    )}

                    {/* Zoom Controls */}
                    <button
                        onClick={(e) => { e.stopPropagation(); handleZoomOut(); }}
                        disabled={zoom <= 1}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${zoom <= 1 ? 'bg-carbon text-iron cursor-not-allowed' : 'bg-graphite hover:bg-iron text-dust'}`}
                        title={zoom <= 1 ? "At minimum zoom (fit to screen)" : "Zoom out"}
                    >
                        <MagnifyingGlassMinusIcon className="w-5 h-5" />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleResetZoom(); }}
                        className="px-3 h-9 rounded-lg bg-graphite hover:bg-iron text-sm font-medium text-dust transition-colors min-w-[60px]"
                        title="Reset zoom"
                    >
                        {Math.round(zoom * 100)}%
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleZoomIn(); }}
                        disabled={zoom >= 5}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${zoom >= 5 ? 'bg-carbon text-iron cursor-not-allowed' : 'bg-graphite hover:bg-iron text-dust'}`}
                        title={zoom >= 5 ? "At maximum zoom" : "Zoom in"}
                    >
                        <MagnifyingGlassPlusIcon className="w-5 h-5" />
                    </button>
                    <div className="w-px h-6 bg-graphite mx-2" />
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-lg bg-graphite hover:bg-iron flex items-center justify-center transition-colors"
                        title="Close (Esc)"
                    >
                        <XIcon className="w-5 h-5 text-dust" />
                    </button>
                </div>
            </div>

            {/* Image Container with Navigation */}
            <div
                ref={viewerRef}
                className="flex-1 flex items-center justify-center overflow-hidden p-4 relative"
                onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e); }}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onWheel={handleWheel}
                onClick={(e) => e.stopPropagation()}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
                {/* Previous Button */}
                {hasMultiple && (
                    <button
                        onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-carbon/80 hover:bg-graphite backdrop-blur-sm flex items-center justify-center transition-colors z-10"
                        title="Previous (←)"
                    >
                        <CaretLeftIcon className="w-6 h-6 text-dust" />
                    </button>
                )}

                <img
                    src={currentImage.url}
                    alt={currentImage.label || 'Image'}
                    className="select-none"
                    style={{
                        transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                        transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                        maxHeight: zoom <= 1 ? '100%' : 'none',
                        maxWidth: zoom <= 1 ? '100%' : 'none',
                        objectFit: 'contain'
                    }}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                />

                {/* Next Button */}
                {hasMultiple && (
                    <button
                        onClick={(e) => { e.stopPropagation(); goToNext(); }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-carbon/80 hover:bg-graphite backdrop-blur-sm flex items-center justify-center transition-colors z-10"
                        title="Next (→)"
                    >
                        <CaretRightIcon className="w-6 h-6 text-dust" />
                    </button>
                )}
            </div>

            {/* Hint */}
            {!isDragging && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg bg-carbon/80 text-xs text-white/70">
                    Scroll to zoom • Drag to pan{hasMultiple ? ' • Arrow keys to navigate' : ''} • Esc to close
                </div>
            )}
        </div>
    );
};
